import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ProjetoEquipamento,
  useProjetoEquipamentos,
  useAdicionarEquipamentoProjeto,
  useAtualizarEquipamentoProjeto,
  useRemoverEquipamentoProjeto,
} from "@/hooks/useProjetos";
import { useEquipamentos } from "@/hooks/useEquipamentos";
import { Loader2, Trash2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EditarProjetoEquipamentosDialogProps {
  projetoId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditarProjetoEquipamentosDialog({
  projetoId,
  open,
  onOpenChange,
}: EditarProjetoEquipamentosDialogProps) {
  const { data: equipamentosProjeto = [], isLoading: isLoadingEquipamentosProjeto, refetch } = useProjetoEquipamentos(projetoId);
  const { data: equipamentosCatalogo = [], isLoading: isLoadingCatalogo } = useEquipamentos();
  const adicionarEquipamento = useAdicionarEquipamentoProjeto();
  const atualizarEquipamento = useAtualizarEquipamentoProjeto();
  const removerEquipamento = useRemoverEquipamentoProjeto();

  const [novoEquipamento, setNovoEquipamento] = useState({
    equipamento_id: "",
    quantidade: 1,
    preco_unitario: "",
    observacoes: "",
  });

  const equipamentosAgrupados = useMemo(() => {
    const agrupados: Record<string, Array<{ id: number; descricao: string; preco?: number }>> = {};

    equipamentosCatalogo.forEach((eq) => {
      const key = eq.tipo?.toLowerCase() || "outros";
      if (!agrupados[key]) agrupados[key] = [];
      agrupados[key].push({
        id: Number(eq.id),
        descricao: `${eq.marca} ${eq.modelo}${eq.potencia ? ` - ${eq.potencia}` : ""}`,
        preco: eq.preco_unitario ? Number(eq.preco_unitario) : undefined,
      });
    });

    return agrupados;
  }, [equipamentosCatalogo]);

  const tiposEquipamento = Object.keys(equipamentosAgrupados);

  const handleAdicionarEquipamento = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!novoEquipamento.equipamento_id) {
      toast.error("Selecione um equipamento");
      return;
    }

    if (novoEquipamento.quantidade <= 0) {
      toast.error("Informe uma quantidade válida");
      return;
    }

    try {
      await adicionarEquipamento.mutateAsync({
        projetoId,
        equipamentoId: Number(novoEquipamento.equipamento_id),
        quantidade: novoEquipamento.quantidade,
        preco_unitario: novoEquipamento.preco_unitario ? Number(novoEquipamento.preco_unitario) : undefined,
        observacoes: novoEquipamento.observacoes || undefined,
      });

      toast.success("Equipamento adicionado ao projeto!");
      setNovoEquipamento({
        equipamento_id: "",
        quantidade: 1,
        preco_unitario: "",
        observacoes: "",
      });
      refetch();
    } catch (error: any) {
      console.error("Erro ao adicionar equipamento:", error);
      toast.error("Erro ao adicionar equipamento: " + error.message);
    }
  };

  const handleAtualizarEquipamento = async (
    item: ProjetoEquipamento,
    campo: "quantidade" | "preco_unitario" | "observacoes",
    valor: string | number
  ) => {
    try {
      await atualizarEquipamento.mutateAsync({
        id: item.id,
        dados: {
          quantidade: campo === "quantidade" ? Number(valor) : item.quantidade,
          preco_unitario: campo === "preco_unitario" ? Number(valor) : item.preco_unitario || undefined,
          observacoes: campo === "observacoes" ? String(valor) : item.observacoes || undefined,
        },
      });
      refetch();
    } catch (error: any) {
      console.error("Erro ao atualizar equipamento:", error);
      toast.error("Erro ao atualizar equipamento: " + error.message);
    }
  };

  const handleRemoverEquipamento = async (item: ProjetoEquipamento) => {
    try {
      await removerEquipamento.mutateAsync({ id: item.id });
      toast.success("Equipamento removido do projeto");
      refetch();
    } catch (error: any) {
      console.error("Erro ao remover equipamento:", error);
      toast.error("Erro ao remover equipamento: " + error.message);
    }
  };

  const loading = isLoadingEquipamentosProjeto || isLoadingCatalogo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Equipamentos do Projeto #{projetoId}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-green-600" />
            <span className="ml-2 text-gray-600">Carregando equipamentos...</span>
          </div>
        ) : (
          <div className="space-y-6">
            <form onSubmit={handleAdicionarEquipamento} className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">
              <div className="md:col-span-2">
                <Label>Equipamento</Label>
                <Select
                  value={novoEquipamento.equipamento_id}
                  onValueChange={(value) => setNovoEquipamento((prev) => ({ ...prev, equipamento_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um equipamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposEquipamento.map((tipo) => (
                      <div key={tipo}>
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                          {tipo}
                        </div>
                        {equipamentosAgrupados[tipo].map((item) => (
                          <SelectItem key={item.id} value={String(item.id)}>
                            <div className="flex flex-col">
                              <span>{item.descricao}</span>
                              {item.preço && (
                                <span className="text-xs text-gray-400">
                                  R$ {Number(item.preço).toFixed(2).replace(".", ",")}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  min={1}
                  value={novoEquipamento.quantidade}
                  onChange={(e) => setNovoEquipamento((prev) => ({ ...prev, quantidade: Number(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Preço Unitário (opcional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={novoEquipamento.preco_unitario}
                  onChange={(e) => setNovoEquipamento((prev) => ({ ...prev, preco_unitario: e.target.value }))}
                />
              </div>
              <div className="md:col-span-3">
                <Label>Observações (opcional)</Label>
                <Input
                  value={novoEquipamento.observacoes}
                  onChange={(e) => setNovoEquipamento((prev) => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Ex: Incluir cabos adicionais"
                />
              </div>
              <div className="md:col-span-1 flex items-end">
                <Button type="submit" className="w-full">
                  Adicionar Equipamento
                </Button>
              </div>
            </form>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Equipamentos Vinculados</h3>
              <ScrollArea className="h-[320px] pr-4">
                {equipamentosProjeto.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg">
                    Nenhum equipamento vinculado a este projeto.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {equipamentosProjeto.map((item) => (
                      <div key={item.id} className="border rounded-lg p-4 bg-white shadow-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold text-gray-900">
                              {item.equipamento?.marca} {item.equipamento?.modelo}
                            </div>
                            <div className="text-sm text-gray-500">
                              Tipo: {item.equipamento?.tipo || "-"}
                              {item.equipamento?.potencia && (
                                <span className="ml-2">Potência: {item.equipamento.potencia}</span>
                              )}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoverEquipamento(item)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                          <div>
                            <Label className="text-xs text-gray-500">Quantidade</Label>
                            <Input
                              type="number"
                              min={1}
                              value={item.quantidade}
                              onChange={(e) =>
                                handleAtualizarEquipamento(item, "quantidade", Number(e.target.value))
                              }
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Preço Unitário (R$)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.preco_unitario || ""}
                              onChange={(e) =>
                                handleAtualizarEquipamento(item, "preco_unitario", Number(e.target.value))
                              }
                              placeholder="Opcional"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Observações</Label>
                            <Input
                              value={item.observacoes || ""}
                              onChange={(e) =>
                                handleAtualizarEquipamento(item, "observacoes", e.target.value)
                              }
                              placeholder="Opcional"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

